"use client";
import { useEffect, useRef } from "react";
import {
  ShieldAlert,
  UserCog,
  Landmark,
  Receipt,
  Gavel,
  Network,
} from "lucide-react";

const TILES = [
  {
    icon: ShieldAlert,
    title: "Sanctions",
    body: "Global lists (OpenSanctions) + MHA Banned Orgs, MHA Disqualified Directors U/S 164(2)(A).",
  },
  {
    icon: UserCog,
    title: "PEPs",
    body: "Indian PEPs (MyNeta / ADR) + 200K+ global PEPs from Wikidata, EveryPolitician, UN, CIA.",
  },
  {
    icon: Landmark,
    title: "Indian regulators",
    body: "13 registers, 300K+ entities. MCA, ED, SEBI, IBBI, RBI, NIA, CBI, BSE, NSE, DGARM and more.",
  },
  {
    icon: Receipt,
    title: "GSTIN",
    body: "Live GSTN verification with cancelled-firm detection.",
  },
  {
    icon: Gavel,
    title: "Litigation",
    body: "Live court records across all Indian states and union territories.",
  },
  {
    icon: Network,
    title: "Director network",
    body: "4-hop network risk BFS. 225K+ debarred directors indexed.",
  },
];

export default function ProblemsSection() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll(".scroll-reveal");
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) {
      targets.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-revealed");
          else entry.target.classList.remove("is-revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }
    );
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section ref={rootRef} className="section-pad bg-white">
      <div className="container-c">
        <div className="scroll-reveal mx-auto max-w-3xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue">What RiskIntel screens</p>
          <h2 className="mt-3 font-display text-[32px] leading-[1.08] tracking-tight text-balance text-ink sm:text-[40px] md:text-[44px]">
            13 Indian regulators. 300K+ entities. One query.
          </h2>
          <p className="mt-4 text-[16px] leading-relaxed text-muted text-pretty">
            Global sanctions, the full Indian regulatory stack, live eCourts and live GSTIN. Plus the director network mapping no global tool offers.
          </p>
        </div>

        <div className="reveal-grid mt-12 grid gap-4 md:mt-14 md:grid-cols-2 lg:grid-cols-3">
          {TILES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="group scroll-reveal flex flex-col items-center text-center rounded-2xl border border-line bg-white p-6 shadow-card hover:border-blue/40"
            >
              <div className="icon-tile grid h-11 w-11 place-items-center rounded-xl bg-blue-soft text-blue transition-colors duration-200">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-[16px] font-semibold text-ink">{title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
